import React, { useState, useEffect } from 'react';
import './AccountInfo.css';

const AccountInfo = ({ customer, updateCustomer, refreshCustomer }) => {
  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [skinType, setSkinType] = useState('');

  useEffect(() => {
    if (customer) {
      setEditData({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
      });
      setSkinType(customer.metadata?.skin_type || '');
    }
  }, [customer]);

  const handleEditChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const saveInfo = async (e) => {
    e.preventDefault();
    setSaving(true);

    const result = await updateCustomer({
      first_name: editData.first_name,
      last_name: editData.last_name,
      phone: editData.phone,
    });

    setSaving(false);

    if (result.success) {
      setEditingSection(null);
      showMessage('success', 'Información actualizada correctamente');
    } else {
      showMessage('error', result.error || 'Error al actualizar');
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage('error', 'Las contraseñas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      showMessage('error', 'La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setSaving(true);

    try {
      showMessage('error', 'El cambio de contraseña debe hacerse desde "¿Olvidaste tu contraseña?"');
      setEditingSection(null);
    } catch (err) {
      showMessage('error', err.message);
    } finally {
      setSaving(false);
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  };

  const saveSkinType = async (e) => {
    e.preventDefault();
    setSaving(true);

    const result = await updateCustomer({
      metadata: {
        ...customer?.metadata,
        skin_type: skinType,
      },
    });

    setSaving(false);

    if (result.success) {
      setEditingSection(null);
      showMessage('success', 'Perfil de piel actualizado');
    } else {
      showMessage('error', result.error || 'Error al actualizar');
    }
  };



  // Función para mostrar el tipo de piel en español con mayúscula
  const getSkinTypeLabel = (type) => {
    const labels = {
      seca: 'Seca',
      mixta: 'Mixta',
      grasa: 'Grasa',
      sensible: 'Sensible',
      normal: 'Normal',
    };
    return labels[type] || '';
  };

  return (
    <div className="account-info">
      {message.text && (
        <div className={`account-info__message account-info__message--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Información Personal */}
      <section className="account-info__section">
        <div className="account-info__row">
          <div className="account-info__content">
            <span className="account-info__label">Información personal</span>
            {editingSection !== 'info' ? (
              <div className="account-info__data">
                <p className="account-info__name">
                  {customer?.first_name} {customer?.last_name}
                </p>
                <p className="account-info__email">{customer?.email}</p>
                {customer?.phone && (
                  <p className="account-info__phone">{customer.phone}</p>
                )}
              </div>
            ) : (
              <form className="account-info__form" onSubmit={saveInfo}>
                <div className="account-info__form-row">
                  <div className="account-info__field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      name="first_name"
                      value={editData.first_name}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="account-info__field">
                    <label>Apellido</label>
                    <input
                      type="text"
                      name="last_name"
                      value={editData.last_name}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                </div>
                <div className="account-info__field">
                  <label>Correo electrónico</label>
                  <input
                    type="email"
                    name="email"
                    value={editData.email}
                    disabled
                    className="account-info__field--disabled"
                  />
                  <span className="account-info__field-hint">
                    El correo no se puede modificar
                  </span>
                </div>
                <div className="account-info__field">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    name="phone"
                    value={editData.phone}
                    onChange={handleEditChange}
                    placeholder="+52 55 1234 5678"
                  />
                </div>
                <div className="account-info__form-actions">
                  <button 
                    type="submit" 
                    className="account-info__btn-save"
                    disabled={saving}
                  >
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            )}
          </div>
          {editingSection !== 'info' && (
            <button
              className="account-info__edit-btn"
              onClick={() => setEditingSection('info')}
            >
              Editar
            </button>
          )}
        </div>
      </section>

      {/* Contraseña */}
      <section className="account-info__section">
        <div className="account-info__row">
          <div className="account-info__content">
            <span className="account-info__label">Contraseña</span>
            {editingSection !== 'password' ? (
              <div className="account-info__data">
                <p className="account-info__password">••••••••••</p>
              </div>
            ) : (
              <form className="account-info__form" onSubmit={savePassword}>
                <div className="account-info__field">
                  <label>Contraseña actual</label>
                  <input
                    type="password"
                    name="oldPassword"
                    value={passwordData.oldPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="account-info__field">
                  <label>Nueva contraseña</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength={8}
                  />
                  <span className="account-info__field-hint">
                    Mínimo 8 caracteres
                  </span>
                </div>
                <div className="account-info__field">
                  <label>Confirmar nueva contraseña</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="account-info__form-actions">
                  <button 
                    type="submit" 
                    className="account-info__btn-save"
                    disabled={saving}
                  >
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            )}
          </div>
          {editingSection !== 'password' && (
            <button
              className="account-info__edit-btn"
              onClick={() => setEditingSection('password')}
            >
              Editar
            </button>
          )}
        </div>
      </section>

      {/* Describe tu piel */}
      <section className="account-info__section">
        <div className="account-info__row">
          <div className="account-info__content">
            <span className="account-info__label">Describe tu piel</span>
            {editingSection !== 'skin' ? (
              <div className="account-info__data">
                <p className="account-info__skin">
                  {customer?.metadata?.skin_type 
                    ? getSkinTypeLabel(customer.metadata.skin_type)
                    : 'Añadir perfil de piel'
                  }
                </p>
              </div>
            ) : (
              <form className="account-info__form" onSubmit={saveSkinType}>
                <div className="account-info__field">
                  <div className="account-info__select-wrapper">
                    <select
                      className="account-info__skin-select"
                      value={skinType}
                      onChange={(e) => setSkinType(e.target.value)}
                    >
                      <option value="">Selecciona tu tipo de piel</option>
                      <option value="seca">Seca</option>
                      <option value="mixta">Mixta</option>
                      <option value="grasa">Grasa</option>
                      <option value="sensible">Sensible</option>
                      <option value="normal">Normal</option>
                    </select>
                  </div>
                </div>
                <div className="account-info__form-actions">
                  <button 
                    type="submit" 
                    className="account-info__btn-save"
                    disabled={saving || !skinType}
                  >
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            )}
          </div>
          {editingSection !== 'skin' && (
            <button
              className="account-info__edit-btn"
              onClick={() => setEditingSection('skin')}
            >
              Editar
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default AccountInfo;
